<template>
  <div class="star-rating">
    <div class="star-rating__stars" role="radiogroup" :aria-labelledby="labelledBy">
      <button
        v-for="star in max"
        :key="star"
        :ref="`star-${star}`"
        type="button"
        role="radio"
        class="star-rating__star"
        :class="{ 'star-rating__star--on': star <= value }"
        :aria-label="`${star}`"
        :aria-checked="star === value"
        :tabindex="isTabStop(star) ? 0 : -1"
        @click="$emit('input', star)"
        @keydown.left.prevent="move(star, 1)"
        @keydown.up.prevent="move(star, 1)"
        @keydown.right.prevent="move(star, -1)"
        @keydown.down.prevent="move(star, -1)"
      >
        <svg class="star-rating__icon" :viewBox="STAR_VIEW_BOX" fill="currentColor" aria-hidden="true">
          <path :d="STAR_PATH" />
        </svg>
      </button>
    </div>
    <div v-if="worstLabel || bestLabel" class="star-rating__scale">
      <span>{{ worstLabel }}</span>
      <span>{{ bestLabel }}</span>
    </div>
  </div>
</template>

<script>
import { STAR_PATH, STAR_VIEW_BOX } from '@/assets/icons/star-path';

// 1..max stars with cumulative fill; v-model gives the picked score (0 = none).
export default {
  name: 'StarRating',
  props: {
    value: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 5,
    },
    worstLabel: {
      type: String,
      default: '',
    },
    bestLabel: {
      type: String,
      default: '',
    },
    // id of the element that asks the question, for the group's accessible name
    labelledBy: {
      type: String,
      default: null,
    },
  },
  data() {
    return { STAR_PATH, STAR_VIEW_BOX };
  },
  methods: {
    // Radiogroup pattern: one tab stop (the picked star, or the first), arrows move and
    // select. The row is RTL: star 1 is rightmost, so Left goes up the scale, Right down.
    isTabStop(star) {
      return this.value ? star === this.value : star === 1;
    },
    move(star, step) {
      const next = Math.min(this.max, Math.max(1, star + step));
      this.$emit('input', next);
      const [el] = this.$refs[`star-${next}`] || [];
      if (el) {
        el.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';
@import '~@riseupil/base-ui/src/scss/typography';

.star-rating {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 100%;
  direction: rtl;
}

.star-rating__stars,
.star-rating__scale {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.star-rating__star {
  width: 37px;
  height: 37px;
  padding: 3.2px 3.86px 5.74px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: rgba($riseup_gray_20, 0.58);
}

.star-rating__star--on {
  color: $riseup_yellow;
}

.star-rating__icon {
  display: block;
  width: 100%;
  height: 100%;
}

.star-rating__scale {
  font-size: $ri-font-size-12;
  line-height: 16px;
  color: $riseup_black;
}
</style>
